<script setup lang="ts">
  import type { IDestinationItem, TDirection } from '@/components/clp/types'
  import type { IClpInfluencerDestinations } from '@/components/clp/influencer-block/types'
  import { ONE_WAY } from '@/components/clp/constants'
  import useCLPDataLayer from '@/composables/data-layers/useCLPDataLayer'

  const props = defineProps<{
    item: IDestinationItem | IClpInfluencerDestinations
    selectedDirection: TDirection
    buttonCategory: string
  }>()

  const { locale, t } = useI18n()
  const url = useRequestURL()
  const route = useRoute()

  const globalSettingsStore = useGlobalSettingsStore()
  const useOldFsf = ref(globalSettingsStore.useOldFsf)

  const runtimeConfig = useRuntimeConfig()
  const domain = runtimeConfig.public.siteUrl

  const localePath = useLocalePath()
  const bookFlightPath = computed(() => localePath('book-flight'))

  function getFsfUrl(item: IDestinationItem | IClpInfluencerDestinations) {
    const queryParams = new URLSearchParams({
      originType: 'A',
      originCode: item.originCode,
      destinType: 'A',
      destinCode: item.destinCode,
      tripType: props.selectedDirection,
      selectedMonth: getStartingMonth(item.travelStartDate),
      numAdt: '1',
      numChd: '0',
      numInf: '0',
      return: '',
      voucher: item.voucher || '',
      returnUrl: `${url.origin}${route.path}`
    })

    if (useOldFsf.value) {
      queryParams.set('opentab', 'ovl')
    }

    if (route.query?.sref && typeof route.query.sref === 'string') {
      queryParams.set('sref', route.query.sref as string)
    }

    if (item.urlParams) {
      const additionalParams = new URLSearchParams(item.urlParams)
      additionalParams.forEach((value, key) => {
        queryParams.set(key, value)
      })
    }

    if (useOldFsf.value) {
      return `${domain}/${locale.value}/index?${queryParams.toString()}`
    }

    return `${domain}${bookFlightPath.value}?${queryParams.toString()}`
  }

  const dayjs = useDayjs()

  function getStartingMonth(item: string | undefined) {
    if (item) {
      const currentDate = dayjs().startOf('month')
      const travelDate = dayjs(item).startOf('month')

      if (travelDate.isSame(currentDate, 'month')) {
        return '0'
      }

      const monthDifference = travelDate.diff(currentDate, 'month')

      return monthDifference.toString()
    }

    return '0'
  }

  async function bookNow() {
    const url = getFsfUrl(props.item)
    if (!url) {
      return
    }

    onBookNowDataLayer()

    await navigateTo(url, { external: true })
  }

  const { pushCLPWithOriginDataLayer } = useCLPDataLayer()
  function onBookNowDataLayer() {
    pushCLPWithOriginDataLayer({
      generic_event_name: 'book_now_button',
      event_category: props.buttonCategory,
      trip_type: props.selectedDirection === ONE_WAY ? 'OW' : 'RT',
      destination_airport_code: props.item.destinCode,
      price_from: props.selectedDirection === ONE_WAY ? props.item.priceOW : props.item.priceRT
    })
  }

  const ariaText = computed(() => {
    const directionKey = 'clp.' + props.selectedDirection

    return `${t('clp.bookNow')} ${props.item.destinCity} ${t(directionKey)} ${t('clp.priceFrom')} ${props.selectedDirection === ONE_WAY ? props.item.priceOW : props.item.priceRT}€`
  })
</script>
<template>
  <UiButton
    :title="$t('clp.bookNow')"
    size="lg"
    variant="secondary"
    class="w-full rounded-sm bg-bt-blue-ui"
    :aria-label="ariaText"
    @click="bookNow()"
  />
</template>
